import React,{useCallback,useEffect,useMemo,useState}from"react";
import{
  Autocomplete,Box,Button,Dialog,DialogActions,DialogContent,
  DialogTitle,FormControl,InputLabel,MenuItem,Paper,Select,
  Table,TableBody,TableCell,TableContainer,TableHead,
  TablePagination,TableRow,TextField,Typography
}from"@mui/material";

const API_BASE="/api/purchases/nomina";

const MONTHS=[
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function today(){
  const date=new Date();
  return[
    date.getFullYear(),
    String(date.getMonth()+1).padStart(2,"0"),
    String(date.getDate()).padStart(2,"0")
  ].join("-");
}

function monthFromDate(value){
  return value?String(value).slice(0,7):"";
}

function monthLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  const month=Number(parts[1]);
  return MONTHS[month-1]&&parts[0]
    ?`${MONTHS[month-1]} ${parts[0]}`
    :"-";
}

function dateLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  return parts.length===3
    ?`${parts[2]}/${parts[1]}/${parts[0]}`
    :String(value);
}

function numberValue(value){
  const result=Number(String(value??"0").replace(",","."));
  return Number.isFinite(result)?result:0;
}

function money(value){
  return numberValue(value).toFixed(2);
}

function normalizeDecimal(value){
  let result=String(value??"")
    .replace(/,/g,".")
    .replace(/[^0-9.]/g,"");

  const index=result.indexOf(".");

  if(index!==-1){
    result=result.slice(0,index+1)+result.slice(index+1).replace(/\./g,"");
  }

  const parts=result.split(".");

  if(parts.length===2){
    result=`${parts[0]}.${parts[1].slice(0,2)}`;
  }

  return result;
}

function employeeName(employee){
  return employee?.razon_social||employee?.nombre_comercial||"";
}

function statusLabel(value){
  if(value==="PENDIENTE")return"Pendiente";
  if(value==="ENTREGADO")return"Entregado";
  if(value==="ANULADO")return"Anulado";
  return value||"-";
}

function deliveryLabel(value){
  if(value==="CAJA")return"Caja";
  if(value==="EFECTIVO")return"Efectivo";
  if(value==="BANCO")return"Transferencia";
  return"-";
}

function initialForm(){
  const fecha=today();

  return{
    empleado_id:"",
    fecha,
    periodo_descuento:monthFromDate(fecha),
    monto:"",
    forma_entrega:"",
    codigo_transferencia:"",
    observacion:""
  };
}

function initialDelivery(){
  return{
    periodo_descuento:"",
    forma_entrega:"",
    codigo_transferencia:""
  };
}

function MoneyInput({
  label,value,onChange,dashboardTheme,disabled=false
}){
  return(
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={event=>onChange(normalizeDecimal(event.target.value))}
      onBlur={()=>onChange(value===""?"":money(value))}
      inputProps={{inputMode:"decimal"}}
      sx={dashboardTheme.inputStyles}
    />
  );
}

export default function Advances({
  empresaId,
  token,
  canView,
  canCreate,
  canEdit,
  canDelete,
  dashboardTheme,
  showError,
  showSuccess,
  showInfo,
  parseError,
  isMobile
}){
  const[employees,setEmployees]=useState([]);
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionLoading,setActionLoading]=useState("");
  const[dialogOpen,setDialogOpen]=useState(false);
  const[deliverItem,setDeliverItem]=useState(null);
  const[annulItem,setAnnulItem]=useState(null);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");
  const[form,setForm]=useState(initialForm);
  const[delivery,setDelivery]=useState(initialDelivery);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("TODOS");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);

  const rowsPerPage=isMobile?10:30;

  const employeeMap=useMemo(
    ()=>new Map(
      employees.map(employee=>[
        String(employee.id),
        employee
      ])
    ),
    [employees]
  );

  const selectedEmployee=useMemo(
    ()=>employeeMap.get(String(form.empleado_id))||null,
    [employeeMap,form.empleado_id]
  );

  const filteredItems=useMemo(()=>{
    const query=search.trim().toLowerCase();

    return items.filter(item=>{
      if(estado!=="TODOS"&&item.estado!==estado)return false;
      if(fechaDesde&&item.fecha<fechaDesde)return false;
      if(fechaHasta&&item.fecha>fechaHasta)return false;

      if(query){
        const employee=employeeMap.get(String(item.empleado_id));

        const searchable=[
          employeeName(employee),
          employee?.cedula,
          employee?.ruc,
          employee?.identificacion,
          item.codigo_transferencia,
          item.fecha,
          item.periodo_descuento,
          monthLabel(item.periodo_descuento||item.fecha)
        ].join(" ").toLowerCase();

        if(!searchable.includes(query))return false;
      }

      return true;
    });
  },[items,employeeMap,search,estado,fechaDesde,fechaHasta]);

  const visibleItems=useMemo(()=>{
    const start=page*rowsPerPage;
    return filteredItems.slice(start,start+rowsPerPage);
  },[filteredItems,page,rowsPerPage]);

  const request=useCallback(async(url,options={})=>{
    const response=await fetch(url,{
      ...options,
      credentials:"include",
      headers:{
        ...(token?{Authorization:`Bearer ${token}`}:{ }),
        ...(options.body?{"Content-Type":"application/json"}:{ }),
        ...(options.headers||{})
      }
    });

    const contentType=response.headers.get("content-type")||"";

    const data=contentType.includes("application/json")
      ?await response.json()
      :{error:"El servidor devolvió una respuesta inválida."};

    if(!response.ok){
      const error=data?.error||data?.detail||data?.mensaje;

      const message=typeof error==="string"
        ?error
        :error&&typeof error==="object"
          ?Object.values(error).flat().join(" ")
          :parseError(data);

      throw new Error(message);
    }

    return data;
  },[token,parseError]);

  const loadData=useCallback(async()=>{
    if(!empresaId||!canView){
      setLoading(false);
      return;
    }

    try{
      setLoading(true);

      const params=new URLSearchParams({
        empresa_id:String(empresaId)
      });

      const[catalogs,advances]=await Promise.all([
        request(`${API_BASE}/catalogos/?${params.toString()}`),
        request(`${API_BASE}/anticipos/?${params.toString()}`)
      ]);

      setEmployees(
        Array.isArray(catalogs.empleados)
          ?catalogs.empleados
          :[]
      );

      setItems(
        Array.isArray(advances.results)
          ?advances.results
          :[]
      );
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  },[empresaId,canView,request,showError]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  useEffect(()=>{
    setPage(0);
  },[search,estado,fechaDesde,fechaHasta,rowsPerPage]);

  useEffect(()=>{
    const lastPage=Math.max(
      0,
      Math.ceil(filteredItems.length/rowsPerPage)-1
    );

    if(page>lastPage)setPage(lastPage);
  },[filteredItems.length,rowsPerPage,page]);

  const formSet=(key,value)=>{
    setForm(previous=>({
      ...previous,
      [key]:value
    }));
  };

  const changeAdvanceDate=value=>{
    setForm(previous=>({
      ...previous,
      fecha:value,
      periodo_descuento:
        !previous.periodo_descuento||
        previous.periodo_descuento===monthFromDate(previous.fecha)
          ?monthFromDate(value)
          :previous.periodo_descuento
    }));
  };

  const openCreate=()=>{
    setForm(initialForm());
    setDialogOpen(true);
  };

  const closeCreate=()=>{
    if(saving)return;
    setDialogOpen(false);
    setForm(initialForm());
  };

  const saveAdvance=async()=>{
    if(saving)return;

    if(!form.empleado_id){
      showInfo("Selecciona el empleado.");
      return;
    }

    if(!form.fecha){
      showInfo("Selecciona la fecha del anticipo.");
      return;
    }

    if(!form.periodo_descuento){
      showInfo("Selecciona el período de descuento.");
      return;
    }

    if(form.periodo_descuento<monthFromDate(form.fecha)){
      showInfo("El período de descuento no puede ser anterior a la entrega.");
      return;
    }

    if(numberValue(form.monto)<=0){
      showInfo("El monto debe ser mayor a cero.");
      return;
    }

    if(
      form.forma_entrega==="BANCO"&&
      !form.codigo_transferencia.trim()
    ){
      showInfo("Ingresa la referencia de la transferencia.");
      return;
    }

    try{
      setSaving(true);

      const data=await request(`${API_BASE}/anticipos/`,{
        method:"POST",
        body:JSON.stringify({
          empresa_id:empresaId,
          empleado_id:form.empleado_id,
          fecha:form.fecha,
          periodo_descuento:`${form.periodo_descuento}-01`,
          monto:money(form.monto),
          forma_entrega:form.forma_entrega,
          codigo_transferencia:
            form.forma_entrega==="BANCO"
              ?form.codigo_transferencia.trim()
              :"",
          observacion:form.observacion.trim()
        })
      });

      showSuccess(data.mensaje||"Anticipo registrado correctamente.");
      setDialogOpen(false);
      setForm(initialForm());
      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const openDelivery=item=>{
    if(item.estado!=="PENDIENTE")return;

    setDeliverItem(item);
    setDelivery({
      periodo_descuento:monthFromDate(
        item.periodo_descuento||item.fecha
      ),
      forma_entrega:item.forma_entrega||"",
      codigo_transferencia:item.codigo_transferencia||""
    });
  };

  const closeDelivery=()=>{
    if(actionLoading)return;
    setDeliverItem(null);
    setDelivery(initialDelivery());
  };

  const confirmDelivery=async()=>{
    if(!deliverItem||actionLoading)return;

    if(!delivery.periodo_descuento){
      showInfo("Selecciona el período de descuento.");
      return;
    }

    if(delivery.periodo_descuento<monthFromDate(deliverItem.fecha)){
      showInfo("El descuento no puede corresponder a un período anterior.");
      return;
    }

    if(!["CAJA","EFECTIVO","BANCO"].includes(delivery.forma_entrega)){
      showInfo("Selecciona cómo se entregó el anticipo.");
      return;
    }

    if(
      delivery.forma_entrega==="BANCO"&&
      !delivery.codigo_transferencia.trim()
    ){
      showInfo("Ingresa la referencia de la transferencia.");
      return;
    }

    try{
      setActionLoading(String(deliverItem.id));

      const data=await request(
        `${API_BASE}/anticipos/${deliverItem.id}/entregar/`,
        {
          method:"POST",
          body:JSON.stringify({
            empresa_id:empresaId,
            periodo_descuento:`${delivery.periodo_descuento}-01`,
            forma_entrega:delivery.forma_entrega,
            codigo_transferencia:
              delivery.forma_entrega==="BANCO"
                ?delivery.codigo_transferencia.trim()
                :""
          })
        }
      );

      showSuccess(data.mensaje||"Anticipo entregado correctamente.");
      setDeliverItem(null);
      setDelivery(initialDelivery());
      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading("");
    }
  };

  const openAnnul=item=>{
    if(!["PENDIENTE","ENTREGADO"].includes(item.estado))return;

    setMotivoAnulacion("");
    setAnnulItem(item);
  };

  const closeAnnul=()=>{
    if(actionLoading)return;
    setAnnulItem(null);
    setMotivoAnulacion("");
  };

  const annulAdvance=async()=>{
    if(!annulItem||actionLoading)return;

    if(
      annulItem.estado==="ENTREGADO"&&
      !motivoAnulacion.trim()
    ){
      showInfo("Ingresa el motivo de anulación.");
      return;
    }

    try{
      setActionLoading(String(annulItem.id));

      const data=await request(
        `${API_BASE}/anticipos/${annulItem.id}/anular/`,
        {
          method:"POST",
          body:JSON.stringify({
            empresa_id:empresaId,
            motivo:motivoAnulacion.trim()
          })
        }
      );

      showSuccess(data.mensaje||"Anticipo anulado correctamente.");
      setAnnulItem(null);
      setMotivoAnulacion("");
      await loadData();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading("");
    }
  };

  const primaryButtonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:34,
    px:1.7,
    borderRadius:"9px",
    textTransform:"none",
    fontSize:12.5,
    fontWeight:600,
    boxShadow:"none"
  };

  const outlinedButtonSx={
    minHeight:32,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontSize:12,
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const dangerButtonSx={
    ...outlinedButtonSx,
    color:"#D32F2F",
    borderColor:"#D32F2F",
    "&:hover":{
      borderColor:"#D32F2F",
      bgcolor:"rgba(211,47,47,0.08)",
      boxShadow:"none"
    }
  };

  const mutedSx={
    fontSize:12.5,
    color:dashboardTheme.muted_text_color
  };

  const inputStyles=dashboardTheme.inputStyles;
  const tableStyles=dashboardTheme.tableStyles;

  const estadoColor=value=>{
    if(value==="ENTREGADO")return"#16A34A";
    if(value==="ANULADO")return"#D32F2F";
    return"#D97706";
  };

  const menuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  const dialogPaperProps={
    elevation:0,
    sx:{
      bgcolor:dashboardTheme.card_background,
      color:dashboardTheme.text_color,
      border:`1px solid ${dashboardTheme.border_color}`,
      borderRadius:"12px",
      boxShadow:"none"
    }
  };

  return(
    <>
      <Box sx={{
        display:"flex",
        alignItems:{xs:"stretch",sm:"center"},
        justifyContent:"space-between",
        flexDirection:{xs:"column",sm:"row"},
        gap:1.5,
        mb:2
      }}>
        <Typography sx={{
          fontSize:{xs:24,sm:26,md:30},
          fontWeight:600,
          color:dashboardTheme.primary_color
        }}>
          Anticipos
        </Typography>

        {canCreate&&(
          <Button
            variant="contained"
            onClick={openCreate}
            sx={primaryButtonSx}
          >
            Registrar anticipo
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{
        ...tableStyles.wrapper,
        boxShadow:"none"
      }}>
        <Box sx={{
          ...tableStyles.toolbar,
          display:"flex",
          flexWrap:"wrap",
          gap:1
        }}>
          <TextField
            size="small"
            label="Buscar empleado o referencia"
            value={search}
            onChange={event=>setSearch(event.target.value)}
            sx={{
              width:{xs:"100%",sm:290,md:350},
              ...inputStyles
            }}
          />

          <FormControl size="small" sx={{
            minWidth:{xs:"100%",sm:150},
            ...inputStyles
          }}>
            <InputLabel>Estado</InputLabel>

            <Select
              label="Estado"
              value={estado}
              onChange={event=>setEstado(event.target.value)}
              MenuProps={menuProps}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="ENTREGADO">Entregado</MenuItem>
              <MenuItem value="ANULADO">Anulado</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Desde"
            value={fechaDesde}
            onChange={event=>setFechaDesde(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...inputStyles
            }}
          />

          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={fechaHasta}
            onChange={event=>setFechaHasta(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...inputStyles
            }}
          />
        </Box>

        <Box sx={tableStyles.tableArea}>
          <TableContainer sx={tableStyles.container}>
            <Table size="small" sx={tableStyles.table}>
              <TableHead>
                <TableRow sx={tableStyles.headRow}>
                  <TableCell sx={{
                    ...tableStyles.headCell,
                    minWidth:190
                  }}>
                    Empleado
                  </TableCell>
                  <TableCell sx={tableStyles.headCell}>
                    Fecha
                  </TableCell>
                  <TableCell sx={tableStyles.headCell}>
                    Período de descuento
                  </TableCell>
                  <TableCell align="right" sx={tableStyles.headCell}>
                    Monto
                  </TableCell>
                  <TableCell align="right" sx={tableStyles.headCell}>
                    Descontado
                  </TableCell>
                  <TableCell sx={tableStyles.headCell}>
                    Entrega
                  </TableCell>
                  <TableCell sx={tableStyles.headCell}>
                    Referencia
                  </TableCell>
                  <TableCell sx={tableStyles.headCell}>
                    Estado
                  </TableCell>
                  <TableCell align="right" sx={tableStyles.headCell}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleItems.map(item=>{
                  const employee=employeeMap.get(
                    String(item.empleado_id)
                  );

                  const anulable=[
                    "PENDIENTE","ENTREGADO"
                  ].includes(item.estado);

                  return(
                    <TableRow
                      key={item.id}
                      hover
                      sx={tableStyles.bodyRow}
                    >
                      <TableCell>
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:600,
                          color:dashboardTheme.text_color
                        }}>
                          {employeeName(employee)||"Empleado"}
                        </Typography>

                        {(employee?.identificacion||employee?.cedula)&&(
                          <Typography sx={{
                            fontSize:11,
                            color:dashboardTheme.muted_text_color,
                            mt:.2
                          }}>
                            {employee.identificacion||employee.cedula}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {dateLabel(item.fecha)}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {monthLabel(
                          item.periodo_descuento||item.fecha
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:700,
                          color:dashboardTheme.text_color
                        }}>
                          ${money(item.monto)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        ${money(item.total_descontado)}
                      </TableCell>

                      <TableCell sx={{fontSize:12.5}}>
                        {deliveryLabel(item.forma_entrega)}
                      </TableCell>

                      <TableCell sx={{fontSize:12.5}}>
                        {item.codigo_transferencia||"-"}
                      </TableCell>

                      <TableCell>
                        <Typography sx={{
                          fontSize:12.5,
                          fontWeight:600,
                          color:estadoColor(item.estado)
                        }}>
                          {statusLabel(item.estado)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{
                          display:"flex",
                          justifyContent:"flex-end",
                          alignItems:"center",
                          gap:.7
                        }}>
                          {canEdit&&item.estado==="PENDIENTE"&&(
                            <Button
                              variant="contained"
                              size="small"
                              onClick={()=>openDelivery(item)}
                              disabled={Boolean(actionLoading)}
                              sx={primaryButtonSx}
                            >
                              Entregar
                            </Button>
                          )}

                          {canDelete&&anulable&&(
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={()=>openAnnul(item)}
                              disabled={Boolean(actionLoading)}
                              sx={dangerButtonSx}
                            >
                              Anular
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading&&filteredItems.length===0&&(
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={tableStyles.emptyCell}
                    >
                      {canView
                        ?"No hay anticipos registrados."
                        :"No tienes permisos para consultar anticipos."}
                    </TableCell>
                  </TableRow>
                )}

                {loading&&(
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={tableStyles.emptyCell}
                    >
                      Cargando anticipos...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_event,nextPage)=>setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
          sx={tableStyles.pagination}
        />
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={closeCreate}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Registrar anticipo
        </DialogTitle>

        <DialogContent>
          <Box sx={{
            display:"grid",
            gridTemplateColumns:{
              xs:"1fr",
              sm:"repeat(2,minmax(0,1fr))"
            },
            gap:1.5,
            pt:1
          }}>
            <Box sx={{
              gridColumn:{xs:"auto",sm:"span 2"}
            }}>
              <Autocomplete
                options={employees}
                value={selectedEmployee}
                getOptionLabel={employee=>{
                  const identificacion=
                    employee.identificacion||
                    employee.cedula||
                    employee.ruc||
                    "";

                  return`${employeeName(employee)}${identificacion?` - ${identificacion}`:""}`;
                }}
                isOptionEqualToValue={(option,value)=>
                  String(option.id)===String(value.id)
                }
                onChange={(_event,value)=>
                  formSet("empleado_id",value?.id||"")
                }
                filterOptions={(options,{inputValue})=>{
                  const query=inputValue.toLowerCase().trim();

                  if(!query)return options.slice(0,50);

                  return options.filter(item=>
                    employeeName(item).toLowerCase().includes(query)||
                    String(item.cedula||"").includes(query)||
                    String(item.ruc||"").includes(query)
                  ).slice(0,50);
                }}
                renderInput={params=>(
                  <TextField
                    {...params}
                    size="small"
                    label="Empleado"
                    placeholder="Buscar empleado..."
                    sx={inputStyles}
                  />
                )}
                noOptionsText="No se encontraron empleados"
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha del anticipo"
              value={form.fecha}
              onChange={event=>changeAdvanceDate(event.target.value)}
              InputLabelProps={{shrink:true}}
              sx={inputStyles}
            />

            <MoneyInput
              label="Monto del anticipo"
              value={form.monto}
              onChange={value=>formSet("monto",value)}
              dashboardTheme={dashboardTheme}
            />

            <TextField
              fullWidth
              size="small"
              type="month"
              label="Período de descuento"
              value={form.periodo_descuento}
              onChange={event=>formSet(
                "periodo_descuento",
                event.target.value
              )}
              inputProps={{
                min:monthFromDate(form.fecha)
              }}
              InputLabelProps={{shrink:true}}
              sx={inputStyles}
            />

            <FormControl
              fullWidth
              size="small"
              sx={inputStyles}
            >
              <InputLabel>Forma de entrega prevista</InputLabel>

              <Select
                label="Forma de entrega prevista"
                value={form.forma_entrega}
                onChange={event=>setForm(previous=>({
                  ...previous,
                  forma_entrega:event.target.value,
                  codigo_transferencia:
                    event.target.value==="BANCO"
                      ?previous.codigo_transferencia
                      :""
                }))}
                MenuProps={menuProps}
              >
                <MenuItem value="">Sin definir</MenuItem>
                <MenuItem value="CAJA">Caja</MenuItem>
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="BANCO">Transferencia bancaria</MenuItem>
              </Select>
            </FormControl>

            {form.forma_entrega==="BANCO"&&(
              <TextField
                fullWidth
                size="small"
                label="Referencia de transferencia"
                value={form.codigo_transferencia}
                onChange={event=>formSet(
                  "codigo_transferencia",
                  event.target.value
                )}
                sx={inputStyles}
              />
            )}

            <Box sx={{
              gridColumn:{xs:"auto",sm:"span 2"}
            }}>
              <TextField
                fullWidth
                size="small"
                label="Observación (opcional)"
                value={form.observacion}
                onChange={event=>formSet(
                  "observacion",
                  event.target.value
                )}
                sx={inputStyles}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{p:2,pt:1,gap:.7}}>
          <Button
            variant="outlined"
            onClick={closeCreate}
            disabled={saving}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveAdvance}
            disabled={saving||!canCreate}
            sx={primaryButtonSx}
          >
            {saving?"Guardando...":"Registrar anticipo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deliverItem)}
        onClose={closeDelivery}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Confirmar entrega
        </DialogTitle>

        <DialogContent>
          {deliverItem&&(
            <Typography sx={{
              fontSize:13,
              fontWeight:600,
              color:dashboardTheme.text_color,
              mb:2
            }}>
              {employeeName(
                employeeMap.get(String(deliverItem.empleado_id))
              )}
              {" · "}
              ${money(deliverItem.monto)}
            </Typography>
          )}

          <Box sx={{
            display:"grid",
            gap:1.5,
            pt:1
          }}>
            <TextField
              fullWidth
              size="small"
              type="month"
              label="Período de descuento"
              value={delivery.periodo_descuento}
              onChange={event=>setDelivery(previous=>({
                ...previous,
                periodo_descuento:event.target.value
              }))}
              inputProps={{
                min:monthFromDate(deliverItem?.fecha)
              }}
              InputLabelProps={{shrink:true}}
              sx={inputStyles}
            />

            <FormControl
              fullWidth
              size="small"
              sx={inputStyles}
            >
              <InputLabel>Método de entrega</InputLabel>

              <Select
                label="Método de entrega"
                value={delivery.forma_entrega}
                onChange={event=>setDelivery(previous=>({
                  ...previous,
                  forma_entrega:event.target.value,
                  codigo_transferencia:
                    event.target.value==="BANCO"
                      ?previous.codigo_transferencia
                      :""
                }))}
                MenuProps={menuProps}
              >
                <MenuItem value="CAJA">Caja</MenuItem>
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="BANCO">Transferencia bancaria</MenuItem>
              </Select>
            </FormControl>

            {delivery.forma_entrega==="BANCO"&&(
              <TextField
                fullWidth
                size="small"
                label="Código de transferencia"
                value={delivery.codigo_transferencia}
                onChange={event=>setDelivery(previous=>({
                  ...previous,
                  codigo_transferencia:event.target.value
                }))}
                sx={inputStyles}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{p:2,pt:1,gap:.7}}>
          <Button
            variant="outlined"
            onClick={closeDelivery}
            disabled={Boolean(actionLoading)}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={confirmDelivery}
            disabled={
              Boolean(actionLoading)||
              !delivery.forma_entrega||
              !delivery.periodo_descuento
            }
            sx={primaryButtonSx}
          >
            {actionLoading?"Confirmando...":"Confirmar entrega"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(annulItem)}
        onClose={closeAnnul}
        fullWidth
        maxWidth="xs"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Anular anticipo
        </DialogTitle>

        <DialogContent>
          {annulItem&&(
            <Typography sx={{
              fontSize:13,
              color:dashboardTheme.text_color,
              mb:2
            }}>
              {dateLabel(annulItem.fecha)}
              {" · "}
              ${money(annulItem.monto)}
            </Typography>
          )}

          {annulItem?.estado==="ENTREGADO"&&(
            <TextField
              fullWidth
              size="small"
              label="Motivo de anulación"
              multiline
              minRows={2}
              value={motivoAnulacion}
              onChange={event=>setMotivoAnulacion(
                event.target.value
              )}
              sx={inputStyles}
            />
          )}
        </DialogContent>

        <DialogActions sx={{p:2,pt:1,gap:.7}}>
          <Button
            variant="outlined"
            onClick={closeAnnul}
            disabled={Boolean(actionLoading)}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={annulAdvance}
            disabled={
              Boolean(actionLoading)||
              (
                annulItem?.estado==="ENTREGADO"&&
                !motivoAnulacion.trim()
              )
            }
            sx={{
              ...primaryButtonSx,
              bgcolor:"#D32F2F",
              "&:hover":{
                bgcolor:"#B71C1C",
                boxShadow:"none"
              }
            }}
          >
            {actionLoading?"Anulando...":"Anular anticipo"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}